import { useEffect, useState } from 'react';
import { masterDataService } from '@/features/master-data/services/master-data-service';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  TransportMode,
  FreightLegType,
} from '@/features/master-data/types/freight-route.types';
import {
  TRANSPORT_MODE_LABELS,
  FREIGHT_LEG_TYPE_LABELS,
} from '@/features/master-data/types/freight-route.types';
import type { ChargeMethod, CreateQuoteFreightLegData } from '../types/quote-freight.types';
import { CHARGE_METHOD_LABELS } from '../types/quote-freight.types';

interface AddFreightLegDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: CreateQuoteFreightLegData) => void;
  nextLegOrder: number;
}

const RATE_LABELS: Record<ChargeMethod, string> = {
  CBM: '$/CBM',
  KG: '$/KG',
  FLAT: 'Monto Fijo',
  MANUAL: 'Monto Manual',
};

export function AddFreightLegDialog({
  open,
  onClose,
  onAdd,
  nextLegOrder,
}: AddFreightLegDialogProps) {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [locations, setLocations] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    masterDataService
      .getFreightLocations()
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [open]);
  const [transportMode, setTransportMode] = useState<TransportMode>('SEA');
  const [legType, setLegType] = useState<FreightLegType>('INTERNATIONAL');
  const [chargeMethod, setChargeMethod] = useState<ChargeMethod>('CBM');
  const [rate, setRate] = useState('');

  const resetForm = () => {
    setOrigin('');
    setDestination('');
    setTransportMode('SEA');
    setLegType('INTERNATIONAL');
    setChargeMethod('CBM');
    setRate('');
  };

  const handleSubmit = () => {
    const rateValue = parseFloat(rate) || 0;
    const data: CreateQuoteFreightLegData = {
      legOrder: nextLegOrder,
      origin: origin.trim(),
      destination: destination.trim(),
      transportMode,
      legType,
      chargeMethod,
      ...(chargeMethod === 'CBM' && { ratePerCbm: rateValue }),
      ...(chargeMethod === 'KG' && { ratePerKg: rateValue }),
      ...(chargeMethod === 'FLAT' && { flatRate: rateValue }),
      ...(chargeMethod === 'MANUAL' && { manualAmount: rateValue }),
    };
    onAdd(data);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canSubmit = origin.trim() !== '' && destination.trim() !== '';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Agregar Tramo de Flete</DialogTitle>
          <DialogDescription>
            Un tramo es un segmento del viaje de la mercadería. Ej: transporte terrestre de fábrica
            al puerto, flete marítimo internacional, o transporte del puerto al almacén.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Origin */}
          <div className="space-y-1.5">
            <Label className="text-sm">Origen</Label>
            <Input
              list="freight-locations"
              placeholder="Elegí o tipeá una ubicación"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Sugerencias de rutas existentes</p>
          </div>

          {/* Destination */}
          <div className="space-y-1.5">
            <Label className="text-sm">Destino</Label>
            <Input
              list="freight-locations"
              placeholder="Elegí o tipeá una ubicación"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Sugerencias de rutas existentes</p>
          </div>

          <datalist id="freight-locations">
            {locations.map((loc) => (
              <option key={loc} value={loc} />
            ))}
          </datalist>

          {/* Transport Mode */}
          <div className="space-y-1.5">
            <Label className="text-sm">Modo de Transporte</Label>
            <Select
              value={transportMode}
              onValueChange={(v) => setTransportMode(v as TransportMode)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TRANSPORT_MODE_LABELS) as TransportMode[]).map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {TRANSPORT_MODE_LABELS[mode]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Cómo se transporta: barco, avión, camión, etc.
            </p>
          </div>

          {/* Leg Type */}
          <div className="space-y-1.5">
            <Label className="text-sm">Tipo de Tramo</Label>
            <Select value={legType} onValueChange={(v) => setLegType(v as FreightLegType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(FREIGHT_LEG_TYPE_LABELS) as FreightLegType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {FREIGHT_LEG_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Define quién paga según el incoterm: interno origen, internacional, interno destino, o
              manipuleo
            </p>
          </div>

          {/* Charge Method */}
          <div className="space-y-1.5">
            <Label className="text-sm">Método de Cobro</Label>
            <Select value={chargeMethod} onValueChange={(v) => setChargeMethod(v as ChargeMethod)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CHARGE_METHOD_LABELS) as ChargeMethod[]).map((method) => (
                  <SelectItem key={method} value={method}>
                    {CHARGE_METHOD_LABELS[method]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {chargeMethod === 'CBM' &&
                'Se multiplica la tarifa × volumen total (m³) de la cotización'}
              {chargeMethod === 'KG' &&
                'Se multiplica la tarifa × peso total (kg) de la cotización'}
              {chargeMethod === 'FLAT' && 'Monto fijo independiente del peso o volumen'}
              {chargeMethod === 'MANUAL' && 'Usted ingresa el monto total directamente'}
            </p>
          </div>

          {/* Rate */}
          <div className="space-y-1.5">
            <Label className="text-sm">{RATE_LABELS[chargeMethod]}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {chargeMethod === 'CBM' && 'Precio en USD por metro cúbico'}
              {chargeMethod === 'KG' && 'Precio en USD por kilogramo'}
              {chargeMethod === 'FLAT' && 'Monto fijo total en USD'}
              {chargeMethod === 'MANUAL' && 'Monto total en USD que desea asignar'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            Agregar Tramo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
